import { TooltipProvider } from '@/components/ui/tooltip';
import { updateProductVariantPrice } from '@/api/sale-api';
import { toast } from '@/hooks/use-toast';
import { useSaleFilteredRows } from '@/pages/sale/hooks/use-sale-filtered-rows';
import { toPinnedVariantStub } from '@/pages/sale/lib/sale-stock-utils';
import { useSalePins, useSaleProducts } from '@/pages/sale/context/sale-providers';
import { Product, ProductVariant } from '@/types/product-type';
import { useCallback, useMemo, useState } from 'react';
import { PinnedVariantBoard } from './pinned-variant-board';
import { PriceEditDialog } from './price-edit-dialog';
import { ProductSaleGridViewV2 } from './product-sale-grid-view-v2';
import { ProductSaleSearchUnpin } from './product-sale-search-unpin';
import { ProductSaleTabsV2 } from './product-sale-tabs-v2';
import { SkuAlphabetFilter } from './sku-alphabet-filter';
import { ThumbnailLightboxProvider } from './product-thumbnail-lightbox-context';

export type ProductSaleTableProps = {
  productView: 'grid' | 'list';
  selectedProducts: string[];
  onToggleProductSelect: (productId: string) => void;
  onRefresh?: (variantId: string) => void;
  isSearchOpen: boolean;
  onSearchOpenChange: (open: boolean) => void;
  pinWrapMode?: boolean;
};

export default function ProductSaleTable({
  productView,
  selectedProducts,
  onToggleProductSelect,
  onRefresh,
  isSearchOpen,
  onSearchOpenChange,
  pinWrapMode = false,
}: ProductSaleTableProps) {
  const {
    totalProduct,
    isLoadingProduct,
    isRefreshingProduct,
    offsetProduct,
    limitProduct,
    onPageChange,
    selectedLetter,
    setSelectedLetter,
    alphabets,
  } = useSaleProducts();

  const {
    activePinTab,
    setActivePinTab,
    pinnedVariantTabs,
    handleTogglePin,
    handleReorder,
    pinnedIdSet,
    isPinned,
    handleRemovePin,
  } = useSalePins();

  const filteredRows = useSaleFilteredRows();
  const [curentProductVariant, setCurrentProductVariant] = useState<ProductVariant | null>(null);

  const availableLetters = useMemo(() => {
    if (alphabets) return new Set(alphabets);
    const letters = new Set<string>();
    filteredRows.forEach(({ p, v }) => {
      const sku = (v.sku || p.code || '').toUpperCase();
      const firstChar = sku.charAt(0);
      if (/[A-Z]/.test(firstChar)) {
        letters.add(firstChar);
      }
    });
    return letters;
  }, [alphabets, filteredRows]);

  const handleSavePrice = async (newPrice: number) => {
    if (!curentProductVariant) return;
    const resp = await updateProductVariantPrice(curentProductVariant.id, newPrice);
    if (resp.success) {
      setCurrentProductVariant(null);
      toast({
        title: 'Success',
        description: 'Product price updated successfully',
      });
      onRefresh?.(curentProductVariant.id);
    } else {
      toast({
        title: 'Error',
        description: resp.message || 'Product price updated failed',
        variant: 'destructive',
      });
    }
  };

  const handleSelectSearch = useCallback(
    async (variantId: string, productId: string, product: Product, variant: ProductVariant) => {
      setSelectedLetter(null);
      setActivePinTab(variantId);
      onSearchOpenChange(false);
      if (!isPinned(variantId)) {
        await handleTogglePin(
          variantId,
          productId,
          true,
          toPinnedVariantStub(variantId, product, variant),
        );
      }
    },
    [isPinned, handleTogglePin, setActivePinTab, setSelectedLetter, onSearchOpenChange],
  );

  const handleSelectAllSearch = useCallback(
    async (
      items: { variantId: string; productId: string; sku: string; product: Product; variant: ProductVariant }[],
    ) => {
      if (items.length === 0) return;
      setSelectedLetter(null);
      const last = items[items.length - 1];
      setActivePinTab(last.variantId);
      onSearchOpenChange(false);
      for (const item of items) {
        if (!isPinned(item.variantId)) {
          await handleTogglePin(
            item.variantId,
            item.productId,
            true,
            toPinnedVariantStub(item.variantId, item.product, item.variant),
          );
        }
      }
    },
    [isPinned, handleTogglePin, setActivePinTab, setSelectedLetter, onSearchOpenChange],
  );

  const handleTabRemovePin = useCallback(
    (id: string, productId: string) => {
      void handleRemovePin(id, productId, { keepActiveTab: activePinTab !== id });
    },
    [activePinTab, handleRemovePin],
  );

  return (
    <ThumbnailLightboxProvider>
      <TooltipProvider delayDuration={250}>
        <div className="flex w-full max-w-full flex-1 flex-col overflow-visible rounded-xl border border-slate-200 bg-white md:h-full md:min-h-0 md:overflow-hidden">
          <ProductSaleTabsV2
            activeTab={activePinTab}
            onTabChange={setActivePinTab}
            onRemovePin={handleTabRemovePin}
            onReorderPins={handleReorder}
            pinnedVariants={pinnedVariantTabs}
            pinWrapMode={pinWrapMode}
          />

          {activePinTab === 'all' ? (
            <div className="flex flex-1 flex-col overflow-visible md:min-h-0 md:overflow-hidden">
              <div className="shrink-0 border-b border-slate-200 bg-slate-50/50">
                <SkuAlphabetFilter
                  selectedLetter={selectedLetter}
                  onLetterSelect={(letter) => setSelectedLetter(letter)}
                  availableLetters={availableLetters}
                />
              </div>
              <div className="flex w-full flex-1 flex-col overflow-visible md:min-h-0 md:overflow-hidden">
                <ProductSaleGridViewV2
                  rows={filteredRows}
                  selectedIds={selectedProducts}
                  pinnedIdSet={pinnedIdSet}
                  onToggleSelect={onToggleProductSelect}
                  onTogglePin={handleTogglePin}
                  viewMode={productView}
                  totalProduct={totalProduct}
                  limitProduct={limitProduct}
                  offsetProduct={offsetProduct}
                  onPageChange={onPageChange}
                  onRefresh={onRefresh}
                  onEditPrice={setCurrentProductVariant}
                  isLoadingProduct={isLoadingProduct}
                  isRefreshingProduct={isRefreshingProduct}
                />
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <PinnedVariantBoard
                variants={pinnedVariantTabs}
                activeId={activePinTab}
                onRefresh={(variantId) => onRefresh?.(variantId)}
              />
            </div>
          )}

          {curentProductVariant && (
            <PriceEditDialog
              productVariant={curentProductVariant}
              onSave={handleSavePrice}
              key={curentProductVariant.id}
              trigger={curentProductVariant.productName}
              open={!!curentProductVariant}
              onOpenChange={(open) => {
                if (!open) setCurrentProductVariant(null);
              }}
            />
          )}

          <ProductSaleSearchUnpin
            isOpen={isSearchOpen}
            onOpenChange={onSearchOpenChange}
            onSelect={handleSelectSearch}
            onSelectAll={handleSelectAllSearch}
          />
        </div>
      </TooltipProvider>
    </ThumbnailLightboxProvider>
  );
}
